import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Bot,
  User as UserIcon,
  Send,
  Flame,
  ShieldAlert,
  Coins,
  Target,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { postCoachScore, postCoachChat } from "../lib/api";
import CoachStreak from "../components/CoachStreak";

const SESSION_KEY = "goldora_coach_session";
const SCORE_KEY = "goldora_coach_score";

function ensureSession() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `coach-${Date.now()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const QUESTIONS = (lang) => {
  const pt = lang === "pt";
  return [
    { id: "spending_vs_income",  q: pt ? "Você gasta MAIS do que ganha hoje?" : "Do you spend MORE than you earn today?", invert: true },
    { id: "credit_card_debt",    q: pt ? "Tem dívida no cartão de crédito ou cheque especial?" : "Do you carry credit card or overdraft debt?", invert: true },
    { id: "emergency_fund",      q: pt ? "Tem reserva de emergência (6 meses de despesa em ativo líquido)?" : "Do you have an emergency fund (6 months of expenses in liquid assets)?" },
    { id: "monthly_invests",     q: pt ? "Investe alguma coisa todo mês de forma automática?" : "Do you invest something automatically every month?" },
    { id: "tracks_expenses",     q: pt ? "Acompanha seus gastos de perto (papel ou app)?" : "Do you track your expenses closely (paper or app)?" },
    { id: "income_sources",      q: pt ? "Tem mais de uma fonte de renda?" : "Do you have more than one income source?" },
    { id: "lifestyle_inflation", q: pt ? "Quando seu salário aumenta, seu gasto aumenta junto?" : "When your salary goes up, do your expenses follow?", invert: true },
    { id: "long_term_goal",      q: pt ? "Tem uma meta financeira clara para os próximos 5–10 anos?" : "Do you have a clear financial goal for the next 5–10 years?" },
  ];
};

const OPTS = (lang) => {
  const pt = lang === "pt";
  return [
    { v: "yes",       l: pt ? "Sim" : "Yes" },
    { v: "sometimes", l: pt ? "Às vezes" : "Sometimes" },
    { v: "no",        l: pt ? "Não" : "No" },
  ];
};

export default function Coach() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [stage, setStage] = useState("intro"); // intro | quiz | result | chat
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [scoreData, setScoreData] = useState(null);
  const [scoring, setScoring] = useState(false);

  const questions = QUESTIONS(lang);
  const opts = OPTS(lang);

  // Restore last score (so user lands on result/chat directly)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SCORE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.score != null) {
          setScoreData(parsed);
          setStage("result");
        }
      }
    } catch {/* ignore */}
  }, []);

  const onPick = async (v) => {
    const q = questions[stepIdx];
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    if (stepIdx + 1 < questions.length) {
      setStepIdx(stepIdx + 1);
    } else {
      setScoring(true);
      try {
        const data = await postCoachScore({ answers: next, lang });
        setScoreData(data);
        localStorage.setItem(SCORE_KEY, JSON.stringify(data));
        setStage("result");
      } catch {
        setStage("intro");
      } finally {
        setScoring(false);
      }
    }
  };

  const restart = () => {
    localStorage.removeItem(SCORE_KEY);
    setScoreData(null);
    setAnswers({});
    setStepIdx(0);
    setStage("quiz");
  };

  return (
    <div data-testid="page-coach" className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div
          aria-hidden
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #E9B934 0%, transparent 60%)" }}
        />
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-gold-300 font-mono">
            <Sparkles size={11} />
            {pt ? "GOLDORA Coach" : "GOLDORA Coach"}
          </div>
          <h1
            className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] max-w-3xl"
            data-testid="coach-hero-title"
          >
            {pt ? "Antes de multiplicar," : "Before you multiply,"}{" "}
            <span className="italic text-gold-400">{pt ? "pare de perder." : "stop the bleeding."}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-neutral-400 text-base leading-relaxed">
            {pt
              ? "Diagnóstico de saúde financeira em 60 segundos + mentor IA pessoal. Sem pedir CPF, conta ou patrimônio. Conteúdo educacional — não vende produto, não cobra comissão."
              : "Personal financial-health diagnosis in 60 seconds + AI mentor. No ID, no account, no net worth asked. Educational — no product sales, no commission."}
          </p>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
        {stage === "intro" && (
          <Intro onStart={() => setStage("quiz")} pt={pt} />
        )}

        {stage === "quiz" && (
          <Quiz
            stepIdx={stepIdx}
            total={questions.length}
            question={questions[stepIdx]}
            opts={opts}
            onPick={onPick}
            scoring={scoring}
            pt={pt}
            onBack={() => stepIdx > 0 ? setStepIdx(stepIdx - 1) : setStage("intro")}
          />
        )}

        {stage === "result" && scoreData && (
          <Result data={scoreData} pt={pt} onTalk={() => setStage("chat")} onRestart={restart} />
        )}

        {stage === "chat" && (
          <CoachChat scoreData={scoreData} pt={pt} lang={lang} onBack={() => setStage("result")} />
        )}
      </div>
    </div>
  );
}

/* -------------------- Intro -------------------- */
function Intro({ onStart, pt }) {
  const pillars = [
    { Icon: Flame,       t: pt ? "Pare a sangria"     : "Stop the bleeding",      d: pt ? "Equilibre o caixa antes de qualquer investimento."        : "Balance cash flow before any investment." },
    { Icon: ShieldAlert, t: pt ? "Mate a dívida cara"  : "Kill expensive debt",   d: pt ? "Cartão e cheque especial são alavanca contra você."       : "Credit cards and overdraft are leverage against you." },
    { Icon: Coins,       t: pt ? "Construa o colchão" : "Build the cushion",     d: pt ? "Reserva de 6 meses em ativo líquido."                     : "6 months of expenses in a liquid asset." },
    { Icon: Target,      t: pt ? "Multiplique"        : "Multiply",              d: pt ? "Só agora começa o jogo do investimento global."           : "Only now does the global investing game begin." },
  ];
  return (
    <div data-testid="coach-intro">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {pillars.map((p, i) => (
          <div key={i} className="surface p-5 rise-in" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="w-10 h-10 rounded-md bg-gold-500/10 border border-gold-500/30 grid place-items-center text-gold-400">
              <p.Icon size={18} />
            </div>
            <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              {pt ? `Etapa ${i + 1}` : `Step ${i + 1}`}
            </div>
            <div className="mt-1 font-display text-lg text-white">{p.t}</div>
            <div className="mt-1 text-sm text-neutral-400 leading-snug">{p.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap items-center gap-3">
        <button
          onClick={onStart}
          data-testid="coach-start-btn"
          className="group inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-6 py-3 rounded-md transition-colors"
        >
          {pt ? "Fazer diagnóstico em 60s" : "Take 60s diagnosis"}
          <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
        <span className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">
          {pt ? "8 perguntas · sem dado sensível" : "8 questions · no sensitive data"}
        </span>
      </div>
    </div>
  );
}

/* -------------------- Quiz -------------------- */
function Quiz({ stepIdx, total, question, opts, onPick, scoring, pt, onBack }) {
  if (scoring) {
    return (
      <div className="grid place-items-center py-24" data-testid="coach-scoring">
        <Loader2 size={28} className="animate-spin text-gold-400" />
        <div className="mt-4 font-mono uppercase tracking-[0.2em] text-xs text-neutral-400">
          {pt ? "Calculando seu score…" : "Calculating your score…"}
        </div>
      </div>
    );
  }
  const pct = ((stepIdx + 1) / total) * 100;
  return (
    <div className="max-w-2xl mx-auto" data-testid="coach-quiz">
      <div className="flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
        <span>{pt ? `Pergunta ${stepIdx + 1} de ${total}` : `Question ${stepIdx + 1} of ${total}`}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="mt-2 h-1 rounded bg-white/5 overflow-hidden">
        <div className="h-full bg-gold-500 transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>

      <h2
        className="mt-10 font-display text-3xl sm:text-4xl tracking-tight text-white leading-snug rise-in"
        data-testid="coach-question"
        key={`q-${stepIdx}`}
      >
        {question.q}
      </h2>

      <div className="mt-8 grid gap-3" key={`opts-${stepIdx}`}>
        {opts.map((o, i) => (
          <button
            key={o.v}
            onClick={() => onPick(o.v)}
            data-testid={`coach-opt-${o.v}`}
            className="group surface text-left px-5 py-4 hover:border-gold-500/40 hover:bg-gold-500/[0.04] transition-all flex items-center justify-between rise-in"
            style={{ animationDelay: `${i * 90 + 100}ms` }}
          >
            <span className="text-base text-white">{o.l}</span>
            <ArrowRight size={16} className="text-neutral-600 group-hover:text-gold-300 group-hover:translate-x-0.5 transition-all" />
          </button>
        ))}
      </div>

      <button
        onClick={onBack}
        className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-300"
      >
        <ArrowLeft size={12} />
        {pt ? "Voltar" : "Back"}
      </button>
    </div>
  );
}

/* -------------------- Result -------------------- */
const TIER_COLORS = {
  red:     { fg: "#ef4444", border: "border-red-500/30",     bg: "bg-red-500/[0.06]"     },
  orange:  { fg: "#f97316", border: "border-orange-500/30",  bg: "bg-orange-500/[0.06]"  },
  yellow:  { fg: "#eab308", border: "border-yellow-500/30",  bg: "bg-yellow-500/[0.06]"  },
  emerald: { fg: "#10b981", border: "border-emerald-500/30", bg: "bg-emerald-500/[0.06]" },
  gold:    { fg: "#E9B934", border: "border-gold-500/40",    bg: "bg-gold-500/[0.08]"    },
};

function ScoreRing({ score, color }) {
  const r = 70;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <div className="relative grid place-items-center" data-testid="coach-score-ring">
      <svg width="180" height="180" viewBox="0 0 180 180" className="-rotate-90">
        <circle cx="90" cy="90" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="8" fill="none" />
        <circle
          cx="90" cy="90" r={r}
          stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 900ms ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-display text-5xl text-white" style={{ color }}>{score}</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mt-1">/ 100</div>
        </div>
      </div>
    </div>
  );
}

function Result({ data, pt, onTalk, onRestart }) {
  const tone = TIER_COLORS[data.color] || TIER_COLORS.gold;
  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="coach-result">
      {/* LEFT: ring + verdict */}
      <div className={`lg:col-span-5 surface ${tone.border} ${tone.bg} p-8 flex flex-col items-center text-center`}>
        <ScoreRing score={data.score} color={tone.fg} />
        <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: tone.fg }}>
          {pt ? "Score de Saúde Financeira" : "Financial Health Score"}
        </div>
        <div className="mt-2 font-display text-2xl text-white" data-testid="coach-tier-label">
          {data.tier_label}
        </div>
        <p className="mt-3 text-neutral-300 max-w-xs leading-snug">{data.headline}</p>

        <button
          onClick={onTalk}
          data-testid="coach-talk-btn"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-5 py-3 rounded-md transition-colors"
        >
          <Bot size={16} />
          {pt ? "Falar com o Coach IA" : "Talk to AI Coach"}
        </button>
        <button
          onClick={onRestart}
          className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1.5"
        >
          <RefreshCw size={12} />
          {pt ? "Refazer diagnóstico" : "Retake diagnosis"}
        </button>

        <div className="mt-6 w-full">
          <CoachStreak />
        </div>
      </div>

      {/* RIGHT: priorities */}
      <div className="lg:col-span-7">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400">
          {pt ? "Suas prioridades" : "Your priorities"}
        </div>
        <h2 className="mt-1 font-display text-3xl text-white tracking-tight">
          {pt ? "Por onde começar (na ordem)" : "Where to start (in order)"}
        </h2>

        {data.priorities.length === 0 ? (
          <div className="mt-6 surface p-6">
            <div className="text-gold-400 text-sm font-mono uppercase tracking-[0.2em]">
              {pt ? "Tudo verde" : "All green"}
            </div>
            <p className="mt-2 text-neutral-300 leading-relaxed">
              {pt
                ? "Sua base está sólida. Hora de focar em comparações cruzadas globais e estratégias avançadas no nosso assistente de investimento."
                : "Your base is solid. Time to focus on global cross-asset comparisons and advanced strategies in our investment assistant."}
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {data.priorities.map((p, i) => (
              <div
                key={p.id}
                data-testid={`coach-priority-${p.id}`}
                className="surface p-5 hover:border-gold-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-md bg-gold-500/10 border border-gold-500/30 grid place-items-center text-gold-300 font-mono text-sm shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-white">{p.title}</div>
                    <p className="mt-1 text-sm text-neutral-400 leading-snug">{p.desc}</p>
                    <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono text-gold-300 bg-gold-500/[0.08] border border-gold-500/20 px-2.5 py-1 rounded">
                      <ArrowRight size={11} />
                      {p.action}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Coach Chat -------------------- */
function CoachChat({ scoreData, pt, lang, onBack }) {
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content: scoreData
        ? (pt
            ? `Olá! Vi seu score: **${scoreData.score}/100 — ${scoreData.tier_label}**. ${scoreData.headline}\n\nMe conta: o que mais te preocupa hoje? Posso ajudar com dívidas, organização do orçamento, hábitos de consumo, construção de reserva ou plano para investir. **Próximo passo:** descreva em 1 frase sua maior dor financeira agora.`
            : `Hi! Saw your score: **${scoreData.score}/100 — ${scoreData.tier_label}**. ${scoreData.headline}\n\nTell me: what worries you most? I can help with debt, budgeting, spending habits, building a cushion, or planning to invest. **Next step:** describe your biggest financial pain in 1 sentence.`)
        : (pt
            ? "Olá! Sou o GOLDORA Coach — mentor de saúde financeira pessoal. Posso ajudar com dívidas, orçamento, hábitos e plano de longo prazo. **Próximo passo:** me conta sua maior dor financeira hoje."
            : "Hi! I'm GOLDORA Coach — personal financial-health mentor. I help with debt, budgeting, habits, long-term plans. **Next step:** tell me your biggest financial pain today."),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(ensureSession);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  // Auto-send if launched with ?q=... (from VoiceCapture overlay)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (q && q.trim()) {
      window.history.replaceState({}, "", window.location.pathname);
      const text = q.trim();
      setMessages((m) => [...m, { role: "user", content: text }]);
      setLoading(true);
      postCoachChat({ session_id: sessionId, message: text, lang, score_context: scoreData || null })
        .then((res) => setMessages((m) => [...m, { role: "assistant", content: res.reply }]))
        .catch((e) => {
          if (e?.response?.status === 402) {
            setMessages((m) => m.slice(0, -1));
            return;
          }
          setMessages((m) => [
            ...m,
            { role: "assistant", content: pt ? "Tive um problema agora. Tenta de novo em alguns segundos." : "I hit an issue. Please try again in a few seconds." },
          ]);
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await postCoachChat({
        session_id: sessionId,
        message: text,
        lang,
        score_context: scoreData || null,
      });
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      if (e?.response?.status === 402) {
        setMessages((m) => m.slice(0, -1));
        return;
      }
      setMessages((m) => [
        ...m,
        { role: "assistant", content: pt ? "Tive um problema agora. Tenta de novo em alguns segundos." : "I hit an issue. Please try again in a few seconds." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="coach-chat" className="grid lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
        <button
          onClick={onBack}
          className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1.5 mb-4"
        >
          <ArrowLeft size={12} />
          {pt ? "Ver score" : "See score"}
        </button>

        <div className="surface p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400">
            {pt ? "Coach IA" : "AI Coach"}
          </div>
          <h3 className="mt-2 font-display text-xl text-white leading-tight">
            {pt ? "Mentor financeiro pessoal" : "Personal financial mentor"}
          </h3>
          <p className="mt-2 text-sm text-neutral-400 leading-snug">
            {pt
              ? "Não recomenda produto. Não pede dado sensível. Pensa em fluxo de caixa, dívida, reserva e hábitos antes de qualquer investimento."
              : "No product recommendations. No sensitive data. Thinks cash flow, debt, cushion and habits before any investment."}
          </p>

          {scoreData && (
            <div className="mt-5 pt-5 border-t border-white/5">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
                {pt ? "Seu contexto" : "Your context"}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-3xl text-gold-400">{scoreData.score}</span>
                <span className="text-sm text-neutral-400">/ 100 · {scoreData.tier_label}</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <CoachStreak />
        </div>
      </aside>

      <div className="lg:col-span-8 surface p-5 flex flex-col" style={{ minHeight: "60vh" }}>
        <div className="flex-1 overflow-y-auto space-y-4 pr-2 max-h-[60vh]" data-testid="coach-messages">
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} />
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-neutral-400 text-sm">
              <Loader2 size={14} className="animate-spin text-gold-400" />
              <span className="font-mono uppercase tracking-[0.18em] text-xs">
                {pt ? "Coach analisando…" : "Coach is thinking…"}
              </span>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="mt-4 pt-4 border-t border-white/5 flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            data-testid="coach-input"
            rows={1}
            placeholder={pt ? "Ex.: Tenho R$ 8 mil no rotativo, o que faço primeiro?" : "E.g.: I owe $1,500 on credit cards, where do I start?"}
            className="flex-1 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-gold-500/50 resize-none"
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            data-testid="coach-send-btn"
            className="bg-gold-500 hover:bg-gold-400 disabled:opacity-40 disabled:cursor-not-allowed text-black p-3 rounded-md transition-colors"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`coach-bubble-${role}`}>
      <div
        className={`w-8 h-8 rounded-md grid place-items-center shrink-0 border ${
          isUser ? "bg-white/5 border-white/10 text-white" : "bg-gold-500/10 border-gold-500/30 text-gold-300"
        }`}
      >
        {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
      </div>
      <div
        className={`max-w-[85%] rounded-md px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser ? "bg-gold-500/10 border border-gold-500/20 text-white" : "bg-white/[0.03] border border-white/5 text-neutral-100"
        }`}
      >
        {content}
      </div>
    </div>
  );
}
